/*
 * EditorCommands — the application command layer.
 *
 * This is where all editor logic lives. WebMCP tools are thin wrappers over these
 * methods, so the commands can be tested (and reasoned about) without any agent,
 * any transport, and any tool schema.
 *
 * Contract for every command here:
 *   - it validates preconditions and throws a ToolError with a specific code;
 *   - every mutation goes through miniPaint's own undo history, so a human Ctrl+Z
 *     reverses it;
 *   - it returns structured state — never "done" — including `documentRevision`.
 *
 * All miniPaint API use below was read from its v4.14.3 source, not guessed. The
 * relevant facts:
 *   Layers.insert/delete/select/set_opacity/toggle_visibility/layer_clear/move/
 *   add_filter are async and each wraps State.do_action(...) -> undo-integrated.
 *   State.do_action resolves to { status: 'completed' } or { status: 'aborted', reason }.
 *   Layers.get_layer(id) shows a UI error toast when missing, so existence checks
 *   here scan get_layers() instead.
 *   Canvas size lives in AppConfig.WIDTH/HEIGHT and needs GUI.prepare_canvas().
 */

import { ErrorCode, ToolError } from '../webmcp/errors.js'
import { HostAction, UpdateLayerAction, BundleHostAction, SetLayerOrderAction } from './hostAction.js'
import { documentRevision } from './revision.js'

/** Filters miniPaint applies through Layers.add_filter, with their real ranges. */
export const FILTERS = {
  brightness: { min: -100, max: 100 },
  contrast: { min: -100, max: 100 },
  saturate: { min: -100, max: 100 }
}

const MAX_CANVAS_DIMENSION = 8192

/**
 * Social formats, in pixels. Named so an agent can pick one from a human phrase
 * like "make an Instagram square" without inventing dimensions.
 */
export const SOCIAL_PRESETS = {
  instagram_square: { width: 1080, height: 1080, label: 'Instagram square post' },
  instagram_portrait: { width: 1080, height: 1350, label: 'Instagram portrait post' },
  instagram_story: { width: 1080, height: 1920, label: 'Instagram story' },
  facebook_post: { width: 1200, height: 630, label: 'Facebook link post' },
  linkedin_post: { width: 1200, height: 627, label: 'LinkedIn post' },
  x_post: { width: 1600, height: 900, label: 'X / Twitter post' },
  youtube_thumbnail: { width: 1280, height: 720, label: 'YouTube thumbnail' }
}
const DEFAULT_EXPORT_MAX_BYTES = 750_000

/** Text layer scaffolding, matching what miniPaint's own text tool creates. */
const TEXT_LAYER_PARAMS = {
  boundary: 'dynamic',
  kerning: 'metrics',
  text_direction: 'ltr',
  wrap_direction: 'ttb',
  halign: 'left',
  valign: 'top',
  wrap: 'letter'
}

export class EditorCommands {
  /**
   * @param {import('./editorHost.js').EditorHost} host
   * @param {import('./revision.js').DocumentRevision} [revision]
   */
  constructor (host, revision = documentRevision) {
    this.host = host
    this.revision = revision
  }

  // ---------------------------------------------------------------- internals

  /** The miniPaint window, or a precise error if the editor is not up yet. */
  _win () {
    if (!this.host.ready) {
      throw new ToolError(
        ErrorCode.EDITOR_NOT_READY,
        'The image editor is not open. Open the Editor page in the application first.',
        { editorState: this.host.status().state }
      )
    }
    return this.host.win
  }

  /** Sync the revision against outside edits, then return the window. */
  _sync () {
    const win = this._win()
    this.revision.sync(win)
    return win
  }

  _layers (win) { return win.Layers.get_layers() }

  /** Find a layer without triggering miniPaint's error toast. */
  _layer (win, layerId) {
    const layer = this._layers(win).find((l) => String(l.id) === String(layerId))
    if (!layer) {
      throw new ToolError(
        ErrorCode.LAYER_NOT_FOUND,
        `No layer with id ${JSON.stringify(layerId)} exists`,
        { requestedLayerId: layerId, availableLayerIds: this._layers(win).map((l) => l.id) }
      )
    }
    return layer
  }

  /** The layer an operation defaults to when the agent gives no id. */
  _activeLayer (win) {
    const active = win.AppConfig.layer
    if (!active) throw new ToolError(ErrorCode.PRECONDITION_FAILED, 'There is no active layer')
    return active
  }

  _resolveLayer (win, layerId) {
    return layerId === undefined || layerId === null ? this._activeLayer(win) : this._layer(win, layerId)
  }

  /** Reject a write whose caller was looking at an older document. */
  _checkRevision (expectedRevision) {
    if (expectedRevision === undefined || expectedRevision === null) return
    if (expectedRevision !== this.revision.current) {
      throw new ToolError(
        ErrorCode.REVISION_MISMATCH,
        `The document changed since revision ${expectedRevision} (it is now ${this.revision.current}). ` +
        'Re-inspect the document before writing.',
        { expectedRevision, currentRevision: this.revision.current, lastChange: this.revision.lastReason }
      )
    }
  }

  /** Run a miniPaint action and surface an aborted action as a structured error. */
  async _doAction (win, action, what) {
    const outcome = await win.State.do_action(action)
    if (outcome && outcome.status === 'aborted') {
      throw new ToolError(
        ErrorCode.TOOL_ERROR,
        `${what} was rejected by the editor: ${outcome.reason && outcome.reason.message ? outcome.reason.message : 'unknown reason'}`
      )
    }
    return outcome
  }

  /** Same, for miniPaint's own async Layers.* wrappers which call do_action inside. */
  async _doWrapped (promise, what) {
    const outcome = await promise
    if (outcome && outcome.status === 'aborted') {
      throw new ToolError(
        ErrorCode.TOOL_ERROR,
        `${what} was rejected by the editor: ${outcome.reason && outcome.reason.message ? outcome.reason.message : 'unknown reason'}`
      )
    }
    return outcome
  }

  /**
   * Force miniPaint to measure text layers whose size is still unknown.
   *
   * A text layer with `boundary: 'dynamic'` gets its width/height computed during a
   * render, and rendering is driven by requestAnimationFrame — which a background or
   * headless tab throttles. Without this an agent that adds text and immediately
   * inspects it sees `width: null` and cannot centre or place anything relative to it.
   * Compositing into a throwaway canvas runs the same measurement synchronously.
   *
   * This populates derived geometry only: no history entry, no revision change
   * (DocumentRevision.signature deliberately ignores geometry).
   */
  _measure (win) {
    const needsMeasuring = this._layers(win)
      .some((l) => l.type === 'text' && (l.width === null || l.height === null))
    if (!needsMeasuring) return
    const canvas = win.document.createElement('canvas')
    canvas.width = Math.max(1, win.AppConfig.WIDTH)
    canvas.height = Math.max(1, win.AppConfig.HEIGHT)
    try {
      win.Layers.convert_layers_to_canvas(canvas.getContext('2d'))
    } catch (e) {
      // Measurement is a convenience; never fail a tool because of it.
    }
  }

  /** Finish a mutation: repaint, advance the revision, and hand back fresh state. */
  _commit (win, payload, reason) {
    win.AppConfig.need_render = true
    win.Layers.render()
    const revision = this.revision.commit(win, reason)
    return { success: true, documentRevision: revision, ...payload }
  }

  /** The public shape of a layer. Stable — tools and tests both depend on it. */
  _describeLayer (layer) {
    const described = {
      id: layer.id,
      name: layer.name,
      type: layer.type === null ? 'empty' : layer.type,
      x: layer.x,
      y: layer.y,
      width: layer.width,
      height: layer.height,
      opacity: layer.opacity,
      visible: layer.visible !== false,
      rotation: layer.rotate || 0,
      order: layer.order,
      filters: (layer.filters || []).map((f) => ({ name: f.name, value: f.params ? f.params.value : undefined }))
    }
    if (layer.type === 'text') described.text = EditorCommands.readText(layer)
    return described
  }

  /** Read the plain text out of miniPaint's line/span text model. */
  static readText (layer) {
    if (!Array.isArray(layer.data)) return ''
    return layer.data
      .map((line) => (Array.isArray(line) ? line.map((span) => span.text || '').join('') : ''))
      .join('\n')
  }

  // -------------------------------------------------------------- read tools

  inspectDocument () {
    const win = this._sync()
    this._measure(win)
    const cfg = win.AppConfig
    const layers = this._layers(win)
    return {
      documentRevision: this.revision.current,
      canvas: { width: cfg.WIDTH, height: cfg.HEIGHT, unit: 'px' },
      layerCount: layers.length,
      activeLayerId: cfg.layer ? cfg.layer.id : null,
      history: {
        undoAvailable: win.State.action_history_index > 0,
        redoAvailable: win.State.action_history_index < win.State.action_history.length,
        undoDepth: win.State.action_history_index,
        redoDepth: win.State.action_history.length - win.State.action_history_index
      },
      editorVersion: cfg.version || null
    }
  }

  inspectLayers () {
    const win = this._sync()
    this._measure(win)
    return {
      documentRevision: this.revision.current,
      activeLayerId: win.AppConfig.layer ? win.AppConfig.layer.id : null,
      // Bottom to top, as the tool description promises. Deliberately NOT
      // miniPaint's get_sorted_layers(), which sorts descending (b.order - a.order)
      // and would hand an agent the stack upside down.
      layers: [...this._layers(win)]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((l) => this._describeLayer(l))
    }
  }

  inspectSelection () {
    const win = this._sync()
    const selection = win.Layers.Base_selection ? win.Layers.Base_selection.get_selection() : null
    const empty = !selection || selection.width === null || selection.height === null ||
      selection.width === 0 || selection.height === 0
    return {
      documentRevision: this.revision.current,
      hasSelection: !empty,
      selection: empty
        ? null
        : { x: selection.x, y: selection.y, width: selection.width, height: selection.height, unit: 'px' },
      activeTool: win.AppConfig.TOOL ? win.AppConfig.TOOL.name : null
    }
  }

  // ------------------------------------------------------------- write tools

  async selectLayer ({ layerId, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const layer = this._layer(win, layerId)
    await this._doWrapped(win.Layers.select(layer.id), 'Selecting the layer')
    return this._commit(win, { layer: this._describeLayer(layer) }, 'select_layer')
  }

  async addTextLayer ({
    text, x = 0, y = 0, fontSize = 40, fontFamily = 'Arial',
    color = '#000000', bold = false, italic = false, name, expectedRevision
  }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)

    // miniPaint stores text as lines of styled spans; a single span is enough here.
    // Its text tool applies metaDefaults for omitted fields, so only set what differs.
    const data = text.split('\n').map((line) => ([{
      text: line,
      meta: { size: fontSize, family: fontFamily, fill_color: color, bold, italic }
    }]))

    await this._doWrapped(win.Layers.insert({
      name: name || `Text: ${text.slice(0, 20)}`,
      type: 'text',
      params: { ...TEXT_LAYER_PARAMS },
      render_function: ['text', 'render'],
      x: Math.round(x),
      y: Math.round(y),
      rotate: 0,
      is_vector: true,
      data
    }), 'Adding the text layer')

    // insert() makes the new layer active; that is how we learn its generated id.
    // (Verified in miniPaint's Insert_layer_action: it returns the do_action envelope,
    // never the layer, and sets config.layer to the created or reused layer.)
    const layer = this._activeLayer(win)
    win.Layers.render()
    this._measure(win)
    return this._commit(win, { layer: this._describeLayer(layer) }, 'add_text_layer')
  }

  async moveLayer ({ layerId, x, y, mode = 'absolute', expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const layer = this._resolveLayer(win, layerId)

    if (layer.x === null || layer.y === null) {
      throw new ToolError(
        ErrorCode.UNSUPPORTED_LAYER_TYPE,
        `Layer ${layer.id} ("${layer.name}") has no position and cannot be moved`,
        { layerId: layer.id, layerType: layer.type }
      )
    }

    const next = mode === 'relative'
      ? { x: Math.round(layer.x + x), y: Math.round(layer.y + y) }
      : { x: Math.round(x), y: Math.round(y) }

    await this._doAction(
      win,
      new UpdateLayerAction(win, layer.id, next, 'Move Layer'),
      'Moving the layer'
    )
    return this._commit(win, { layer: this._describeLayer(this._layer(win, layer.id)) }, 'move_layer')
  }

  async resizeLayer ({ layerId, width, height, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const layer = this._resolveLayer(win, layerId)

    if (layer.width === null || layer.height === null) {
      throw new ToolError(
        ErrorCode.UNSUPPORTED_LAYER_TYPE,
        `Layer ${layer.id} ("${layer.name}") has no size and cannot be resized`,
        { layerId: layer.id, layerType: layer.type }
      )
    }

    const next = {
      width: Math.round(width !== undefined ? width : layer.width),
      height: Math.round(height !== undefined ? height : layer.height)
    }
    await this._doAction(win, new UpdateLayerAction(win, layer.id, next, 'Resize Layer'), 'Resizing the layer')
    return this._commit(win, { layer: this._describeLayer(this._layer(win, layer.id)) }, 'resize_layer')
  }

  async deleteLayer ({ layerId, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const layer = this._layer(win, layerId)

    if (this._layers(win).length <= 1) {
      throw new ToolError(
        ErrorCode.PRECONDITION_FAILED,
        'Refusing to delete the only layer in the document',
        { layerCount: 1 }
      )
    }

    const deleted = this._describeLayer(layer)
    await this._doWrapped(win.Layers.delete(layer.id), 'Deleting the layer')
    return this._commit(win, { deletedLayer: deleted, layerCount: this._layers(win).length }, 'delete_layer')
  }

  async setLayerOpacity ({ layerId, opacity, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const layer = this._resolveLayer(win, layerId)
    // The tool takes 0..1 because that is how agents talk about opacity;
    // miniPaint stores 0..100.
    const percent = Math.round(opacity * 100)
    await this._doWrapped(win.Layers.set_opacity(layer.id, percent), 'Setting layer opacity')
    return this._commit(
      win,
      { layer: this._describeLayer(this._layer(win, layer.id)), opacity, opacityPercent: percent },
      'set_layer_opacity'
    )
  }

  async duplicateLayer ({ layerId, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const source = this._resolveLayer(win, layerId)

    // miniPaint's own duplicate module always acts on the active layer, so select
    // first. Done as a plain copy here to keep the new layer's placement predictable
    // (its module nudges the copy by 10px, which an agent would not expect).
    const settings = {}
    for (const [key, value] of Object.entries(source)) {
      if (key === 'id' || key === 'order' || key.startsWith('_')) continue
      if (key === 'link' && value && typeof value.cloneNode === 'function') {
        settings.link = value.cloneNode(true)
        continue
      }
      settings[key] = value && typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value
    }
    settings.name = `${source.name} copy`

    await this._doWrapped(win.Layers.insert(settings), 'Duplicating the layer')
    const created = this._activeLayer(win)
    return this._commit(
      win,
      { layer: this._describeLayer(created), sourceLayerId: source.id },
      'duplicate_layer'
    )
  }

  // ---------------------------------------------------------- document tools

  async resizeCanvas ({ width, height, scaleLayers = false, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    this._assertCanvasSize(width, height)

    const gui = win.Layers.Base_gui
    const actions = [new CanvasSizeAction(win, gui, Math.round(width), Math.round(height))]

    if (scaleLayers) {
      const widthRatio = win.AppConfig.WIDTH / width
      const heightRatio = win.AppConfig.HEIGHT / height
      const ratio = Math.max(widthRatio, heightRatio)
      for (const layer of this._layers(win)) {
        const settings = {}
        if (layer.x !== null && layer.y !== null) {
          settings.x = Math.round(layer.x / widthRatio)
          settings.y = Math.round(layer.y / heightRatio)
        }
        if (layer.width !== null && layer.height !== null) {
          settings.width = Math.round(layer.width / ratio)
          settings.height = Math.round(layer.height / ratio)
        }
        if (Object.keys(settings).length) {
          actions.push(new UpdateLayerAction(win, layer.id, settings, 'Scale layer with canvas'))
        }
      }
    }

    await this._doAction(
      win,
      new BundleHostAction(actions, 'Resize Canvas', 'resize_canvas'),
      'Resizing the canvas'
    )
    return this._commit(
      win,
      { canvas: { width: win.AppConfig.WIDTH, height: win.AppConfig.HEIGHT, unit: 'px' }, scaledLayers: !!scaleLayers },
      'resize_canvas'
    )
  }

  async cropImage ({ x, y, width, height, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    this._assertCanvasSize(width, height)

    const cfg = win.AppConfig
    if (x < 0 || y < 0 || x + width > cfg.WIDTH || y + height > cfg.HEIGHT) {
      throw new ToolError(
        ErrorCode.INVALID_ARGUMENTS,
        `The crop rectangle must lie inside the ${cfg.WIDTH}x${cfg.HEIGHT} canvas`,
        { canvas: { width: cfg.WIDTH, height: cfg.HEIGHT }, requested: { x, y, width, height } }
      )
    }

    // Crop = move the origin and shrink the canvas. Layer pixels are untouched,
    // so the operation is lossless and exactly reversible by undo.
    const actions = []
    for (const layer of this._layers(win)) {
      if (layer.x === null || layer.y === null) continue
      actions.push(new UpdateLayerAction(
        win, layer.id,
        { x: Math.round(layer.x - x), y: Math.round(layer.y - y) },
        'Offset layer for crop'
      ))
    }
    actions.push(new CanvasSizeAction(win, win.Layers.Base_gui, Math.round(width), Math.round(height)))

    await this._doAction(win, new BundleHostAction(actions, 'Crop Image', 'crop_image'), 'Cropping the image')
    return this._commit(
      win,
      { canvas: { width: cfg.WIDTH, height: cfg.HEIGHT, unit: 'px' }, croppedTo: { x, y, width, height } },
      'crop_image'
    )
  }

  async rotateImage ({ degrees, layerId, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const layer = this._resolveLayer(win, layerId)

    if (layer.rotate === null || layer.rotate === undefined) {
      throw new ToolError(
        ErrorCode.UNSUPPORTED_LAYER_TYPE,
        `Layer ${layer.id} ("${layer.name}") cannot be rotated`,
        { layerId: layer.id, layerType: layer.type }
      )
    }

    let normalized = Math.round(degrees) % 360
    if (normalized < 0) normalized += 360

    await this._doAction(
      win,
      new UpdateLayerAction(win, layer.id, { rotate: normalized }, 'Rotate Layer'),
      'Rotating the layer'
    )
    return this._commit(
      win,
      { layer: this._describeLayer(this._layer(win, layer.id)), rotation: normalized, unit: 'degrees' },
      'rotate_image'
    )
  }

  async adjust ({ filter, value, layerId, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const range = FILTERS[filter]
    if (!range) {
      throw new ToolError(ErrorCode.NOT_SUPPORTED, `Unknown adjustment "${filter}"`, { supported: Object.keys(FILTERS) })
    }
    const layer = this._resolveLayer(win, layerId)
    if (layer.type === null) {
      throw new ToolError(
        ErrorCode.UNSUPPORTED_LAYER_TYPE,
        `Layer ${layer.id} ("${layer.name}") is empty, so an adjustment would have no effect`,
        { layerId: layer.id }
      )
    }

    await this._doWrapped(win.Layers.add_filter(layer.id, filter, { value }), `Applying ${filter}`)
    return this._commit(
      win,
      { layer: this._describeLayer(this._layer(win, layer.id)), adjustment: { filter, value } },
      `adjust_${filter}`
    )
  }

  // ------------------------------------------------------------ shape tools

  /**
   * Paint a solid rectangle onto its own layer.
   *
   * miniPaint has no programmatic fill or shape API that avoids its dialogs, so the
   * rectangle is rasterised here into an offscreen canvas and inserted as a normal
   * image layer. That keeps it a first class layer: movable, reorderable, adjustable,
   * and reversible through the same undo history as everything else.
   */
  _solidCanvasDataUrl (win, width, height, color, cornerRadius = 0) {
    const canvas = win.document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(width))
    canvas.height = Math.max(1, Math.round(height))
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = color

    const radius = Math.min(cornerRadius, canvas.width / 2, canvas.height / 2)
    if (radius > 0 && typeof ctx.roundRect === 'function') {
      ctx.beginPath()
      ctx.roundRect(0, 0, canvas.width, canvas.height, radius)
      ctx.fill()
    } else {
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }
    return canvas.toDataURL('image/png')
  }

  /** The order value that puts a new layer underneath everything else. */
  _backmostOrder (win) {
    const orders = this._layers(win).map((l) => l.order).filter((o) => typeof o === 'number')
    return orders.length ? Math.min(...orders) - 1 : 1
  }

  async addRectangle ({
    x, y, width, height, color, cornerRadius = 0, opacity = 1,
    name, sendToBack = false, expectedRevision
  }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    this._assertCanvasSize(width, height)

    // miniPaint grows the document to fit any inserted image layer bigger than the
    // canvas (Insert_layer_action always builds an Autoresize_canvas_action from the
    // layer's dimensions; can_automate only tunes it). A badge must never resize the
    // banner it sits on, so the size is capped here. Position is NOT capped, because
    // autoresize keys off dimensions only, so shapes may still bleed off the edges.
    const cfg = win.AppConfig
    if (width > cfg.WIDTH || height > cfg.HEIGHT) {
      throw new ToolError(
        ErrorCode.INVALID_ARGUMENTS,
        `A rectangle cannot be larger than the ${cfg.WIDTH}x${cfg.HEIGHT} canvas. ` +
        'Resize the canvas first if the document should be bigger.',
        { requested: { width, height }, canvas: { width: cfg.WIDTH, height: cfg.HEIGHT } }
      )
    }

    const settings = {
      name: name || 'Rectangle',
      type: 'image',
      data: this._solidCanvasDataUrl(win, width, height, color, cornerRadius),
      x: Math.round(x),
      y: Math.round(y),
      width: Math.round(width),
      height: Math.round(height),
      opacity: Math.round(opacity * 100)
    }
    // Insert_layer_action honours an explicit order, so the layer lands at the
    // bottom in the same undo step rather than needing a second reorder.
    if (sendToBack) settings.order = this._backmostOrder(win)

    // can_automate: false disables miniPaint's autoresize-canvas-to-the-new-layer
    // behaviour. Without it, drawing a rectangle resizes the whole document to the
    // rectangle, and the empty starting layer gets silently reused.
    await this._doWrapped(win.Layers.insert(settings, false), 'Adding the rectangle')
    const layer = this._activeLayer(win)
    return this._commit(win, { layer: this._describeLayer(layer) }, 'add_rectangle')
  }

  async setBackgroundColor ({ color, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const cfg = win.AppConfig
    const result = await this.addRectangle({
      x: 0,
      y: 0,
      width: cfg.WIDTH,
      height: cfg.HEIGHT,
      color,
      name: 'Background',
      sendToBack: true
    })
    return { ...result, color, canvas: { width: cfg.WIDTH, height: cfg.HEIGHT, unit: 'px' } }
  }

  async reorderLayer ({ layerId, position, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const layer = this._resolveLayer(win, layerId)

    await this._doAction(
      win,
      new SetLayerOrderAction(win, layer.id, position),
      'Reordering the layer'
    )
    return this._commit(
      win,
      { layer: this._describeLayer(this._layer(win, layer.id)), position, layers: this.inspectLayers().layers },
      'reorder_layer'
    )
  }

  async alignLayer ({ layerId, horizontal, vertical, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    this._measure(win)
    const layer = this._resolveLayer(win, layerId)

    if (layer.width === null || layer.height === null || layer.x === null || layer.y === null) {
      throw new ToolError(
        ErrorCode.UNSUPPORTED_LAYER_TYPE,
        `Layer ${layer.id} ("${layer.name}") has no measurable geometry, so it cannot be aligned`,
        { layerId: layer.id, layerType: layer.type }
      )
    }

    const cfg = win.AppConfig
    const settings = {}
    if (horizontal === 'left') settings.x = 0
    else if (horizontal === 'center') settings.x = Math.round((cfg.WIDTH - layer.width) / 2)
    else if (horizontal === 'right') settings.x = Math.round(cfg.WIDTH - layer.width)

    if (vertical === 'top') settings.y = 0
    else if (vertical === 'middle') settings.y = Math.round((cfg.HEIGHT - layer.height) / 2)
    else if (vertical === 'bottom') settings.y = Math.round(cfg.HEIGHT - layer.height)

    if (!Object.keys(settings).length) {
      throw new ToolError(
        ErrorCode.INVALID_ARGUMENTS,
        'Give at least one of horizontal or vertical',
        { horizontal, vertical }
      )
    }

    await this._doAction(win, new UpdateLayerAction(win, layer.id, settings, 'Align Layer'), 'Aligning the layer')
    return this._commit(
      win,
      { layer: this._describeLayer(this._layer(win, layer.id)), alignedTo: { horizontal, vertical } },
      'align_layer'
    )
  }

  async setText ({ layerId, text, fontSize, fontFamily, color, bold, italic, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const layer = this._resolveLayer(win, layerId)

    if (layer.type !== 'text') {
      throw new ToolError(
        ErrorCode.UNSUPPORTED_LAYER_TYPE,
        `Layer ${layer.id} ("${layer.name}") is not a text layer`,
        { layerId: layer.id, layerType: layer.type === null ? 'empty' : layer.type }
      )
    }

    // Keep the styling the layer already has for anything the caller did not pass.
    const existing = (Array.isArray(layer.data) && layer.data[0] && layer.data[0][0] && layer.data[0][0].meta) || {}
    const meta = { ...existing }
    if (fontSize !== undefined) meta.size = fontSize
    if (fontFamily !== undefined) meta.family = fontFamily
    if (color !== undefined) meta.fill_color = color
    if (bold !== undefined) meta.bold = bold
    if (italic !== undefined) meta.italic = italic

    const content = text !== undefined ? text : EditorCommands.readText(layer)
    const settings = { data: content.split('\n').map((line) => ([{ text: line, meta: { ...meta } }])) }

    await this._doAction(win, new UpdateLayerAction(win, layer.id, settings, 'Edit Text'), 'Updating the text')
    win.Layers.render()
    this._measure(win)
    return this._commit(win, { layer: this._describeLayer(this._layer(win, layer.id)) }, 'set_text')
  }

  async setLayerVisibility ({ layerId, visible, expectedRevision }) {
    const win = this._sync()
    this._checkRevision(expectedRevision)
    const layer = this._resolveLayer(win, layerId)
    await this._doAction(
      win,
      new UpdateLayerAction(win, layer.id, { visible }, visible ? 'Show Layer' : 'Hide Layer'),
      'Changing layer visibility'
    )
    return this._commit(win, { layer: this._describeLayer(this._layer(win, layer.id)) }, 'set_layer_visibility')
  }

  // -------------------------------------------------------- high-level tools

  /**
   * Reshape the current document into a named social format.
   *
   * Composed from the primitives on purpose: it is resizeCanvas with scaleLayers,
   * so it inherits that operation's undo behaviour and its single history entry.
   * The layout the human has arranged is carried across proportionally rather than
   * being rebuilt, which is the whole point of "make a portrait version of this".
   */
  async createSocialVariant ({ preset, scaleLayers = true, expectedRevision }) {
    const target = SOCIAL_PRESETS[preset]
    if (!target) {
      throw new ToolError(
        ErrorCode.NOT_SUPPORTED,
        `Unknown social preset "${preset}"`,
        { supported: Object.keys(SOCIAL_PRESETS) }
      )
    }
    const win = this._sync()
    const from = { width: win.AppConfig.WIDTH, height: win.AppConfig.HEIGHT }

    const result = await this.resizeCanvas({
      width: target.width,
      height: target.height,
      scaleLayers,
      expectedRevision
    })

    return {
      ...result,
      preset,
      presetLabel: target.label,
      resizedFrom: from,
      layers: this.inspectLayers().layers
    }
  }

  // ------------------------------------------------------------------ history

  async undo () {
    const win = this._sync()
    if (win.State.action_history_index <= 0) {
      throw new ToolError(ErrorCode.NOTHING_TO_UNDO, 'There is nothing to undo')
    }
    const undone = win.State.action_history[win.State.action_history_index - 1]
    await win.State.undo()
    return this._commit(
      win,
      { undone: { actionId: undone.action_id, description: undone.action_description }, document: this._documentSnapshot(win) },
      'undo'
    )
  }

  async redo () {
    const win = this._sync()
    if (win.State.action_history_index >= win.State.action_history.length) {
      throw new ToolError(ErrorCode.NOTHING_TO_REDO, 'There is nothing to redo')
    }
    const redone = win.State.action_history[win.State.action_history_index]
    await win.State.redo()
    return this._commit(
      win,
      { redone: { actionId: redone.action_id, description: redone.action_description }, document: this._documentSnapshot(win) },
      'redo'
    )
  }

  _documentSnapshot (win) {
    return {
      canvas: { width: win.AppConfig.WIDTH, height: win.AppConfig.HEIGHT },
      layerCount: this._layers(win).length
    }
  }

  // ------------------------------------------------------------------- export

  /**
   * Composite the document and return it as a data URL.
   *
   * A full-size PNG data URL is megabytes of base64, which is far too much to hand
   * back through a tool result. So the payload is capped: over the limit the tool
   * returns the metadata and tells the agent to pass `maxDimension`.
   */
  exportImage ({ format = 'png', quality = 0.92, maxDimension, maxBytes = DEFAULT_EXPORT_MAX_BYTES } = {}) {
    const win = this._sync()
    const doc = win.document
    const { width, height } = win.Layers.get_dimensions()

    const full = doc.createElement('canvas')
    full.width = width
    full.height = height
    win.Layers.convert_layers_to_canvas(full.getContext('2d'))

    let out = full
    let scale = 1
    if (maxDimension && Math.max(width, height) > maxDimension) {
      scale = maxDimension / Math.max(width, height)
      out = doc.createElement('canvas')
      out.width = Math.max(1, Math.round(width * scale))
      out.height = Math.max(1, Math.round(height * scale))
      const ctx = out.getContext('2d')
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(full, 0, 0, out.width, out.height)
    }

    const mime = format === 'jpeg' ? 'image/jpeg' : format === 'webp' ? 'image/webp' : 'image/png'
    const dataUrl = mime === 'image/png' ? out.toDataURL(mime) : out.toDataURL(mime, quality)
    const byteLength = dataUrl.length
    const tooLarge = byteLength > maxBytes

    return {
      documentRevision: this.revision.current,
      format: mime.replace('image/', ''),
      width: out.width,
      height: out.height,
      sourceWidth: width,
      sourceHeight: height,
      scale,
      byteLength,
      truncated: tooLarge,
      dataUrl: tooLarge ? null : dataUrl,
      ...(tooLarge
        ? { hint: `The encoded image is ${byteLength} bytes, over the ${maxBytes} byte limit. Call again with a smaller maxDimension (for example 512) to receive the data URL.` }
        : {})
    }
  }

  _assertCanvasSize (width, height) {
    for (const [name, value] of [['width', width], ['height', height]]) {
      if (!Number.isFinite(value) || value < 1 || value > MAX_CANVAS_DIMENSION) {
        throw new ToolError(
          ErrorCode.INVALID_ARGUMENTS,
          `${name} must be between 1 and ${MAX_CANVAS_DIMENSION} pixels`,
          { [name]: value, max: MAX_CANVAS_DIMENSION }
        )
      }
    }
  }
}

/**
 * Change the canvas size reversibly.
 *
 * Mirrors what miniPaint does internally (Prepare_canvas_action + Update_config_action):
 * write AppConfig.WIDTH/HEIGHT, then let the GUI rebuild the canvas element.
 */
export class CanvasSizeAction extends HostAction {
  constructor (win, gui, width, height) {
    super('update_canvas_size', 'Resize Canvas')
    this.win = win
    this.gui = gui
    this.width = width
    this.height = height
    this.previous = null
  }

  async do () {
    this.previous = { width: this.win.AppConfig.WIDTH, height: this.win.AppConfig.HEIGHT }
    this._apply(this.width, this.height)
    this.is_done = true
  }

  async undo () {
    if (this.previous) this._apply(this.previous.width, this.previous.height)
    this.is_done = false
  }

  _apply (width, height) {
    this.win.AppConfig.WIDTH = width
    this.win.AppConfig.HEIGHT = height
    if (this.gui && typeof this.gui.prepare_canvas === 'function') this.gui.prepare_canvas()
    this.win.AppConfig.need_render = true
    this.win.Layers.render()
  }
}
